/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import PropTypes from "prop-types";
import _ from "lodash";

import Checkbox from "@material-ui/core/Checkbox";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import MoreIcon from "@material-ui/icons/MoreVert";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import styles from "../../../styles/utils/config.scss";
import { adminWeight, auditorWeight, getMaxWeight, plAdminWeight, viewOnlyWeight } from "../../../roleaccess/utils";
import { updateDocumentLinkStatus, deleteDocumentFromSystem } from "../../../services/document";
import DeleteConfirmationPopup from "../../document/DeleteConfirmationPopup";
import { openModal } from "../../_shared/modal/modalActions";

import * as actions from "../projectActions";
import { deleteDocument } from "../../document/documentActions";
import { downloadDocument } from "../../document/utils";
import { formatDateAndTime } from "../../../utilities";

const statusCodeMap = {
  '0': 'In Progress',
  '1': 'Public Viewable',
  '2': 'Deleted',
};


const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  hoverDocName: {
    display: 'contents',
    "&:hover": {
      color: styles.primaryColor,
    },
  },
  loadingSpinner: {
    marginLeft: 15,
    marginTop: 1,
  },
  docRow: {
    '&:hover': {
      backgroundColor: styles.focusedSelectBackgroundRowColor,
      boxSizing: 'border- box',
      boxShadow: '0 2px 4px 0',
    },
  },
  cell: {
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

export const columnHeaders = [
  "Document Name",
  "Document Status",
  "Created By",
  "Created Date",
  "Last Modified By",
  "Last Modified",
  "Linked Status",
  "Actions",
];

const mapDispatchToProps = dispatch => ({
  isMetaDataView: (metaDataView, document) =>
    dispatch(actions.setMetaDataView(metaDataView, document)),
  openConfirmModal: body => dispatch(openModal({ body })),
  deleteDoc: (documentId, closingId) =>
    dispatch(deleteDocument(documentId, closingId)),
});

export const ProjectDocumentsTable = ({
  documents,
  handleSorting,
  sortDirection,
  openErrorModel,
  isMetaDataView,
  openConfirmModal,
  userRole,
  deleteDoc,
  statusCode,
  onLinkStatusUpdated,
  documentStatusByProject,
  isDocStatusLoading,
  lastOpenedProjectId,
  currentProjectId,
}) => {
  const classes = useStyles();

  const isDocumentLinked = (document) => {
    if (document?.linkStatus === true || document?.linkStatus === 'true') {
      return true;
    }
    if (document?.is_linked_document === true || document?.is_linked_document === 'true') {
      return true;
    }
    return document?.REL_doc_ind === 'Y' || document?.REL_doc_ind === 1 || document?.REL_doc_ind === '1';
  };

  const isLoadingAtStatus = useMemo(() => (lastOpenedProjectId === null || Number(lastOpenedProjectId) !== Number(currentProjectId)) || isDocStatusLoading, [lastOpenedProjectId, currentProjectId, isDocStatusLoading]);

  // const [sortedDocuments, setSortedDocuments] = useState(documents);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [
    selectedDocumentForDownload,
    setSelectedDocumentForDownload,
  ] = useState(null);

  const handleMenuOpen = (event, document) => {
    setSelectedDocument(document);
    setMenuAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const allowedWeights = [adminWeight(), auditorWeight(), plAdminWeight()];
  const canDeleteProject = allowedWeights.includes(getMaxWeight(userRole));
    
  const showDeleteConfirmationPopup = (
    documentId,
    closingId,
    closing_doc_name,
  ) => {
    openConfirmModal(
      <DeleteConfirmationPopup
        onStay={() => confirmDelete(documentId, closingId, closing_doc_name)}
        onDiscard={handleMenuClose}
        documentName={closing_doc_name}
      />,
    );
  };

  const confirmDelete = (documentId, closingId) => {
    deleteDoc(documentId, closingId);
    handleMenuClose();
  };

  const handleUnlink = async document => {
    try {
      // For linked documents, we only call updateDocumentLinkStatus
      // The backend will handle removing the document-project association
      // We do NOT call deleteDocumentFromSystem as that deletes the document from the entire system

      const response = await updateDocumentLinkStatus({
        projectId: Number.parseInt(document.closing_id),
        categoryId: Number.parseInt(document.doc_category_id),
        documents: [
          {
            fileId: String(document.documentum_doc_id || "").trim(),
            fileName: String(document.closing_doc_name || "").trim(),
          },
        ],
        // Always pass false for unlink - the API handles "still linked elsewhere" logic internally
        linkStatus: false,
      });

      const unlinkResult = response?.data?.updateDocumentLinkStatus;
      if (!unlinkResult?.Success) {
        // Log technical error for debugging, but show user-friendly message
        const rawMessage = unlinkResult?.Message || '';
        console.error('[Unlink Document] Unlink failed:', rawMessage);
        
        const isTechnicalError = /permission|denied|database|schema|INSERT|UPDATE|DELETE|SELECT/i.test(rawMessage);
        const userMessage = isTechnicalError
          ? "Failed to unlink document due to a system error. Please contact support."
          : (rawMessage || "Failed to unlink document.");
        openErrorModel("Error", userMessage);
        return;
      }

      handleMenuClose();
      onLinkStatusUpdated();
      openErrorModel("Success", "Document unlinked successfully.");
    } catch (error) {
      openErrorModel("Error", "Unable to unlink document. Please try again.");
    }
  };

  function openMetaDataView(document) {
    isMetaDataView(true, document);
  }
  function renderCell(column, sortDirection) {
    if (column == "Actions") {
      return (
        <TableCell align='center' key={column}>
          <TableSortLabel>{column}</TableSortLabel>
        </TableCell>
      );
    } else {
      return (
        <TableCell key={column} onClick={() => handleSorting(column)}>
          <TableSortLabel direction={sortDirection}>{column}</TableSortLabel>
        </TableCell>
      );
    }
  }

  const requestDownloadDocument = (
    document,
    isLatestVersion,
    openErrorModel,
    setLoading,
  ) => {
    downloadDocument(
      document.documentum_doc_id,
      isLatestVersion,
      openErrorModel,
      setLoading,
    );
  };

  const renderDocNameCell = document => {
    let isLoading = false;
    if (selectedDocumentForDownload) {
      isLoading = true;
    }

    function setLoading(value, documentId) {
      isLoading = value;
      setSelectedDocumentForDownload(documentId);
    }

    return (
      <TableCell component='th' scope='row'>
        <Link
          target='_blank'
          component='button'
          underline='hover'
          color='inherit'
          className={classes.hoverDocName}
          onClick={() =>
            requestDownloadDocument(document, true, openErrorModel, setLoading)
          }>
          {document.closing_doc_name}
        </Link>
        {isLoading &&
          selectedDocumentForDownload &&
          selectedDocumentForDownload == document.documentum_doc_id && (
            <CircularProgress size={20} className={classes.loadingSpinner} />
          )}
      </TableCell>
    );
  };

  return (
    <Paper className={classes.root}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {/* <TableCell padding="checkbox">
              <Checkbox inputProps={{ "aria-label": "Select all" }} />
            </TableCell> */}
            {columnHeaders.map((column, i) =>
              renderCell(column, sortDirection),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {_.map(documents, document => {
            const status = documentStatusByProject?.[document.documentum_doc_id];
            const statusLabel = statusCodeMap?.[status] ?? (status ? `Unknown (${status})` : '-');
            return (
              <TableRow className={classes.docRow} key={document.documentum_doc_id}>
                {/* <TableCell padding="checkbox">
                <Checkbox
                  checked={false}
                  inputProps={{ "aria-labelledby": "" }}
                />
              </TableCell> */}
                {renderDocNameCell(document)}
                <TableCell className={classes.cell}>
                  {isLoadingAtStatus ? (
                    <CircularProgress size={16} />
                  ) :
                    statusLabel
                  }
                </TableCell>
                <TableCell className={classes.cell}>{document.create_userid || ''}</TableCell>
              <TableCell className={classes.cell}>
                {document.create_ts ? formatDateAndTime(document.create_ts) : ''}
              </TableCell>
                {document.last_change_ts != 'Today' && (
                  <>
                    <TableCell className={classes.cell}>{document.last_change_userid}</TableCell>
                    <TableCell className={classes.cell}>
                      {formatDateAndTime(document.last_change_ts)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {isDocumentLinked(document) ? 'Linked' : 'Upload'}
                    </TableCell>
                  </>
                )}
                {document.last_change_ts == 'Today' && (
                  <>
                    <TableCell className={classes.cell}></TableCell>
                    <TableCell className={classes.cell}>
                      {document.last_change_ts}
                    </TableCell>
                  </>
                )}
                <TableCell className={classes.cell} align='center'>
                  <>
                  <IconButton onClick={e => handleMenuOpen(e, document)}>
                    <MoreIcon />
                  </IconButton>
                  <Menu
                    elevation={0}
                    PaperProps={{
                      style: {
                        border: "1px solid #E0E0E0",
                      },
                    }}
                    anchorEl={menuAnchorEl}
                    keepMounted
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}>
                    <MenuItem onClick={() => openMetaDataView(selectedDocument)}>
                      View Info
                    </MenuItem>
                    {canDeleteProject && selectedDocument && (
                      isDocumentLinked(selectedDocument) ? (
                        <MenuItem onClick={() => handleUnlink(selectedDocument)}>
                          Unlink
                        </MenuItem>
                      ) : (
                        documentStatusByProject?.[selectedDocument.documentum_doc_id] !== '1' && (
                          <MenuItem
                            onClick={() =>
                              showDeleteConfirmationPopup(
                                selectedDocument.documentum_doc_id,
                                selectedDocument.closing_id,
                                selectedDocument.closing_doc_name,
                              )
                            }>
                            Delete
                          </MenuItem>
                        )
                      )
                    )}
                  </Menu>
                </>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

ProjectDocumentsTable.defaultProps = {
  documents: [],
  onLinkStatusUpdated: () => {},
};

ProjectDocumentsTable.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      lastModified: PropTypes.string,
      modifiedBy: PropTypes.string,
    }),
  ),
  openErrorModel: PropTypes.func.isRequired,
  onLinkStatusUpdated: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(ProjectDocumentsTable);
